import React, { ChangeEvent, useContext, useMemo, useState } from 'react'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormCheckboxField,
  FormDateField,
  FormField,
  FormFileField,
  FormMultiSelectField,
  FormReadonlyField,
  FormSelectField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import { PromoMaterialsOptionsDto } from 'api/ClientArea/PromoMaterialsOptionsDto'
import { UpdateLandingPageDto } from 'api/ClientArea/UpdateLandingPageDto'
import { Language } from 'api/Clients/Types/Language'
import { TickmillCompanyDto } from 'api/Identity/Types/Company'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { ChevronDown } from 'lucide-react'
import { AuthSessionContext } from 'utils/AuthContext'
import { convertDateToUTC, createDateAsUTC } from 'utils/date'
import { toBase64 } from 'utils/file.helpers'
import { FormSubmitValues } from 'utils/formValidation'
import { isEmpty } from 'utils/is-empty'

import { PermissionDto } from '../../../api/Identity/Types/PermissionDto'
import { PermissionsEnum } from '../../../api/Identity/Types/PermissionsEnum'
import { hasPermission } from '../../../utils/permissionsValidator'

export interface IbLandingDetailsFormValues {
  name: string | undefined
  languageIds: string[]
  baseUrl: number | undefined
  path: string | undefined
  enabled: boolean
  endDateTime: string | undefined
  tickmillCompanyId: number | undefined
  currentImagePath: string | undefined
  landingPageImage: string | undefined
  landingPageFileImage: string | undefined
  order: number | undefined
  isDefault: boolean
}

const IbLandingFormUI: React.FC<FormikProps<IbLandingDetailsFormValues> & OuterProps> = (props) => {
  const { handleSubmit, setFieldValue, errors, values } = props
  const { languages, tickmillCompanies, promoMaterialsOptions, resetForm, permissions } = props

  const [isEdit, setEdit] = useState<boolean>(false)
  const [auth] = useContext(AuthSessionContext)
  const dateFormatType = auth?.user?.dateFormatType?.name
  const languageOptions = useMemo(
    () => languages.map((language) => ({ id: language.id, name: language.name })),
    [languages]
  )
  const baseUrlOptions = useMemo(
    () => promoMaterialsOptions.baseUrls.map((baseUrl) => ({ id: baseUrl.id, name: baseUrl.name })),
    [promoMaterialsOptions.baseUrls]
  )
  const companyOptions = useMemo(
    () => tickmillCompanies.map((company) => ({ id: company.id, name: company.name })),
    [tickmillCompanies]
  )

  const handleSubmitForm = () => {
    handleSubmit()
  }

  const handleCancel = () => {
    setEdit(false)
    resetForm()
  }

  const handleUploadImage = async (event: ChangeEvent<HTMLInputElement>) => {
    const [file] = event.target.files || []
    const base64 = await toBase64(file)

    if (file && base64) {
      setFieldValue('landingPageFileImage', base64)
    }
  }

  const renderMenu = () => (
    <DropdownMenuItem
      onSelect={() => props.setAuditLogModal(true)}
      data-test='audit-log-menu-button'
    >
      Audit Log
    </DropdownMenuItem>
  )

  return (
    <div className='px-4 md:px-6'>
      <Form>
        <DataGrid
          rightAddon={
            <ButtonGroup>
              {isEdit ? (
                <>
                  <Button
                    type='button'
                    variant='outline'
                    className='is-danger'
                    onClick={handleCancel}
                    data-test='cancel-button'
                  >
                    Cancel
                  </Button>
                  <Button type='button' onClick={handleSubmitForm} data-test='save-button'>
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  type='button'
                  onClick={() => setEdit(true)}
                  data-test='edit-button'
                  disabled={!hasPermission(permissions, PermissionsEnum.CanEditPromoMaterial)}
                >
                  Edit
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <Button
                      type='button'
                      variant='outline'
                      size='icon'
                      aria-label='More options'
                      className='more-button'
                    />
                  }
                >
                  <ChevronDown />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>{renderMenu()}</DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          }
        >
          <DataColumn>
            <DataCard title={!isEdit ? 'Landing Page' : 'Edit Landing Page'} columns={2}>
              <FormField
                name='name'
                label='Name'
                error={errors.name}
                readOnly={!isEdit}
                value={values.name}
              />
              <FormMultiSelectField
                name='languageIds'
                label='Language'
                error={errors.languageIds?.toString()}
                value={values.languageIds}
                options={languageOptions}
                readOnly={!isEdit}
                onChange={(value) =>
                  setFieldValue('languageIds', Array.isArray(value) ? value : [])
                }
              />
              <FormSelectField
                name='baseUrl'
                label='Base URL'
                error={errors.baseUrl}
                value={values.baseUrl}
                options={baseUrlOptions}
                readOnly={!isEdit}
                showSearch
                allowClear
                onChange={(value) => setFieldValue('baseUrl', value ?? undefined)}
              />
              <FormField
                name='path'
                label='Path'
                error={errors.path}
                readOnly={!isEdit}
                value={values.path}
              />
              <FormReadonlyField
                name='currentImagePath'
                label='Current Image Path'
                value={values.currentImagePath}
              />
              {isEdit && (
                <FormFileField
                  accept='image/jpeg'
                  onChange={handleUploadImage}
                  name='landingPageImage'
                  label='Choose New Image'
                />
              )}
              <FormSelectField
                name='tickmillCompanyId'
                error={errors.tickmillCompanyId}
                label='Tickmill Entity'
                value={values.tickmillCompanyId}
                options={companyOptions}
                readOnly={!isEdit}
                showSearch
                allowClear
                onChange={(value) => setFieldValue('tickmillCompanyId', value ?? undefined)}
              />
              <FormDateField
                name='endDateTime'
                error={errors.endDateTime}
                label='End Date'
                readOnly={!isEdit}
                dateFormatType={dateFormatType}
              />
              <FormCheckboxField
                name='enabled'
                error={errors.enabled}
                label='Active'
                readOnly={!isEdit}
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
      </Form>
    </div>
  )
}

const dataToSubmit = (values: Required<IbLandingDetailsFormValues>): UpdateLandingPageDto => {
  return {
    name: values.name || '',
    promoMaterialBaseUrlId: values.baseUrl || 0,
    url: values.path || '',
    enabled: values.enabled || false,
    endDateTime: values.endDateTime
      ? createDateAsUTC(values.endDateTime, false, true).toISOString()
      : '',
    tickmillCompanyId: values.tickmillCompanyId || 0,
    languageIds: values.languageIds || [],
    landingPageImage: values.landingPageFileImage || '',
    order: 99999,
    isDefault: false,
  }
}

interface OuterProps {
  data: LandingPageDto
  languages: Language[]
  tickmillCompanies: TickmillCompanyDto[]
  promoMaterialsOptions: PromoMaterialsOptionsDto
  permissions: PermissionDto[]

  setAuditLogModal(value: boolean): void
  onSubmit(values: FormSubmitValues<UpdateLandingPageDto>): Promise<void>
}

export const IbLandingDetailsForm = withFormik<OuterProps, IbLandingDetailsFormValues>({
  mapPropsToValues: (props) => {
    const { data } = props

    return {
      name: data?.name || undefined,
      languageIds: (data.languages || [])?.map((x) => x.id),
      baseUrl: data?.promoMaterialBaseUrl?.id || undefined,
      path: data?.url || undefined,
      enabled: data?.enabled || false,
      endDateTime: data.endDateTime ? convertDateToUTC(data.endDateTime).toISOString() : undefined,
      tickmillCompanyId: data?.tickmillCompany?.id || undefined,
      currentImagePath: data?.imagePath || '',
      landingPageImage: undefined,
      landingPageFileImage: undefined,
      order: data?.order || undefined,
      isDefault: data?.isDefault || false,
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      const data = dataToSubmit(values)
      await props.onSubmit(data)
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<IbLandingDetailsFormValues> = {}

    if (!values.name) {
      errors.name = 'Required'
    }

    if (values.languageIds.length <= 0) {
      errors.languageIds = 'Required'
    }

    if (!values.baseUrl) {
      errors.baseUrl = 'Required'
    }

    if (!values.path) {
      errors.path = 'Required'
    }

    if (!values.landingPageFileImage && !values.currentImagePath) {
      errors.landingPageFileImage = 'Required'
    }

    if (!values.tickmillCompanyId) {
      errors.tickmillCompanyId = 'Required'
    }

    if (isEmpty(values.enabled)) {
      errors.enabled = 'Required'
    }

    return errors
  },
  enableReinitialize: true,
  validateOnChange: true,
})(IbLandingFormUI)
